import { useCallback } from 'react'

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  Heading,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'

import {
  useAtsSyncStats,
  useDisconnectAtsIntegration,
  useTriggerAtsUserSync,
} from '@/react-query/ats/integration'
import { useTeam } from '@/react-query/team'

import { ConnectModal } from './ConnectModal'

export const ATSIntegration = () => {
  const { data: team } = useTeam()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const disconnectMutation = useDisconnectAtsIntegration()
  const syncUsersMutation = useTriggerAtsUserSync()

  const isConnected = team?.isATSConnected || false
  const syncStats = useAtsSyncStats(isConnected)

  const handleDisconnectATS = useCallback(async () => {
    try {
      await disconnectMutation.mutateAsync()

      toast({
        title: 'Success',
        description: 'ATS integration disconnected successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect ATS integration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }, [disconnectMutation, toast])

  const handleConnectATS = useCallback(() => {
    onOpen()
  }, [onOpen])

  const isDisconnectLoading = disconnectMutation.isPending

  const atsName = team?.atsIntegrationName || 'ATS'

  return (
    <FormControl mt={8}>
      <Box mb={8}>
        <Heading size="md">ATS Integration</Heading>
        <Text mt={2} fontSize="sm" color="gray.600">
          Connect your Applicant Tracking System to sync candidate data and
          streamline your hiring process.
        </Text>
      </Box>

      {isConnected ? (
        <>
          <Alert status="success" borderRadius="full" mb={4} maxW="500px">
            <AlertIcon />
            <Text fontSize="sm">{atsName} is connected and ready to use.</Text>
          </Alert>

          {syncStats.data && (
            <Box mb={4} p={3} bg="gray.50" borderRadius="md" maxW="500px">
              <Text fontSize="sm" color="gray.600">
                <Text as="span" fontWeight="600">
                  {syncStats.data.candidateCount}
                </Text>{' '}
                candidates ·{' '}
                <Text as="span" fontWeight="600">
                  {syncStats.data.applicationCount}
                </Text>{' '}
                applications synced
                {syncStats.data.lastSyncAt && (
                  <>
                    {' '}
                    · Last sync{' '}
                    {formatTimeAgo(new Date(syncStats.data.lastSyncAt))}
                  </>
                )}
              </Text>
            </Box>
          )}

          <Box display="flex" gap={3}>
            <Button
              variant="brand-solid-gray"
              onClick={handleDisconnectATS}
              isLoading={isDisconnectLoading}
              loadingText="Disconnecting..."
              minW="170px"
            >
              Disconnect ATS
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={() => syncUsersMutation.mutate()}
              isLoading={syncUsersMutation.isPending}
              loadingText="Syncing..."
            >
              Sync user accounts
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <Button variant="brand-solid" onClick={handleConnectATS} minW="170px">
            Connect ATS
          </Button>
        </Box>
      )}

      <ConnectModal isOpen={isOpen} onClose={onClose} />
    </FormControl>
  )
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'Just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
